import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CheckRounded,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  FunctionsOutlined,
  PendingActionsOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { lwfPaymentHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, SimpleTable, ToneChip, downloadCsv, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FilterSelect, PlainInput, RecordHeaderCard, StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import { FilePick, fileError } from "pages/PayRoll/Statutory/PF/PfMemberPanels";
import {
  PAYMENT_TONES,
  PAYMENT_TYPES,
  PERIOD_FILTERS,
  formatDay,
  inPeriodFilter,
  monthRows,
  money,
  num,
  paymentStatus,
} from "./lwfReportsData";

// Payments (LWF5), Record LWF Payment (LWF5a) and the payment view: one row
// per site per deduction month. Payable = employee shares (including any
// not yet recovered from employees) + employer shares. A saved payment is
// never edited: a wrong one is reversed and recorded again. References are
// unique per company.

const { indigo, green, orange, pink, purple } = detailAccentPalette;
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };

// Company LWF statement (not an official return): one line per covered
// employee of the site and month, with the header the spec asks for.
const downloadStatement = (period, companyName) => {
  const rows = monthRows(period.period_key).filter((row) => row.site_name === period.site && row.ee != null && row.ee + row.er > 0);
  downloadCsv(
    `LWF_statement_${period.site.replace(/\s+/g, "_")}_${period.period_key}.csv`,
    ["Company", "Site", "State", "Registration No.", "Deduction month", "Version", "Employee ID", "Name", "LWF Wage", "Employee", "Employer", "Status"],
    rows.map((row) => [companyName || "", period.site, period.state || "", period.registration_no, period.period_label, period.version_name || "", row.emp_id, row.name, row.lwf_wage, row.ee, row.er, row.status_label]),
  );
};

const RecordPanel = ({ open, period, allPayments, companyName, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open || !period) return;
    setForm({ type: period.paid > 0 ? "BALANCE" : "FIRST", paid_on: "", amount: String(Math.max(0, period.balance)), reference: "", remarks: "" });
    setFile(null);
    setShowErrors(false);
  }, [open, period]);

  if (!period || !form) return null;
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const amount = Number(form.amount) || 0;
  const after = paymentStatus(period.payable, period.paid + amount);
  const reference = form.reference.trim();

  const errors = {};
  if (!form.type) errors.type = "Pick the payment type";
  if (!form.paid_on) errors.paid_on = "Pick the paid date";
  if (!(amount > 0) || !Number.isInteger(amount)) errors.amount = "Whole rupees, more than 0";
  if (!reference) errors.reference = "Enter the reference / challan no.";
  else if (allPayments.some((payment) => payment.reference.toLowerCase() === reference.toLowerCase())) errors.reference = "This reference is already recorded";
  if (fileError(file)) errors.receipt = fileError(file);
  const error = (key) => (showErrors ? errors[key] : undefined);

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({ period: period.id, type: form.type, paid_on: form.paid_on, amount, reference, receipt: file?.name || "", remarks: form.remarks.trim() });
  };

  const typeOptions = period.paid > 0 ? [{ value: "BALANCE", label: PAYMENT_TYPES.BALANCE }] : [{ value: "FIRST", label: PAYMENT_TYPES.FIRST }];
  const statusLine =
    after === "Paid"
      ? "✓ Amount matches — status will be Paid"
      : after === "Partly paid"
        ? `${money(period.payable - period.paid - amount)} short — status will be Partly paid`
        : after === "Overpaid"
          ? `${money(period.paid + amount - period.payable)} more than payable — status will be Overpaid`
          : "Enter the amount paid";

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Record LWF Payment"
      subtitle={`${period.period_label} · ${period.site} · ${period.registration_no}`}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save Payment" primaryProps={{ onClick: save }} />}
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar="LW"
          title={`${period.site} · ${period.period_label}`}
          chip={<ToneChip label={period.status} tone={PAYMENT_TONES[period.status]} />}
          meta={`${num(period.employees)} employees · employee ${money(period.ee)} + employer ${money(period.er)} = ${money(period.payable)} · due ${formatDay(period.due)}`}
          tone="warning"
        />
        <RequestDetailsSection
          title="COMPANY LWF STATEMENT"
          fields={[
            {
              icon: DescriptionOutlined,
              label: "Employees and LWF for this deduction (prepared by your company — not an official return)",
              value: `${num(period.employees)} employees · ${money(period.payable)}`,
              helper: (
                <Mui.Link component="button" underline="hover" onClick={() => downloadStatement(period, companyName)} sx={linkSx}>
                  Download
                </Mui.Link>
              ),
              accent: indigo,
              multiline: true,
            },
            ...(period.outstanding
              ? [{ icon: PendingActionsOutlined, label: "Of which not yet recovered from employees", value: money(period.outstanding), helper: "Already inside the payable — not extra", accent: orange }]
              : []),
            ...(period.paid ? [{ icon: CheckRounded, label: "Already paid", value: money(period.paid), helper: `Balance ${money(period.balance)}`, accent: green }] : []),
          ]}
        />
        <FormSection title="Payment">
          <Field label={<>Payment type<Required /></>} error={error("type")}>
            <SelectField value={form.type} onChange={(value) => value && set("type")(value)} options={typeOptions} error={error("type")} />
          </Field>
          <Field label={<>Paid on<Required /></>} error={error("paid_on")}>
            <DateField value={form.paid_on} onChange={set("paid_on")} maxDate={moment().format("YYYY-MM-DD")} error={error("paid_on")} />
          </Field>
          <Field label={<>Amount paid (₹)<Required /></>} error={error("amount")}>
            <PlainInput type="number" value={form.amount} onChange={set("amount")} error={error("amount")} inputProps={{ min: 1, step: 1 }} />
          </Field>
          <Field label={<>Reference / challan no.<Required /></>} error={error("reference")}>
            <TextField value={form.reference} onChange={set("reference")} error={error("reference")} placeholder="e.g. TNLWB-2026-004512" />
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <FilePick label="Upload receipt or drag & drop" hint="PDF, JPG or PNG · max 5 MB · optional" value={file} error={error("receipt")} onChange={setFile} />
          </Mui.Box>
          <Field wide label="Remarks">
            <TextField value={form.remarks} onChange={set("remarks")} placeholder="—" />
          </Field>
          <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1", fontWeight: designWeights.semibold, color: after === "Paid" ? "#067647" : "#B54708" }}>
            {statusLine}
          </Mui.Typography>
        </FormSection>
        <RequestInfoBanner body="A saved payment is never edited: use Reverse and record again. Paid short? Record a Balance payment later." />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const ViewPanel = ({ open, period, companyName, onClose, onRecord, onReverse }) => {
  const [reverseTarget, setReverseTarget] = useState(null);
  if (!period) return null;
  const reversed = new Set(period.payments.filter((payment) => payment.reverses_id).map((payment) => payment.reverses_id));
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="LWF Payment"
      subtitle={`${period.period_label} · ${period.site} · ${period.registration_no}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          {period.balance > 0 ? <SecondaryButton onClick={onRecord}>Record payment</SecondaryButton> : null}
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar="LW"
          title={`${period.site} · ${period.period_label}`}
          chip={<ToneChip label={period.status} tone={PAYMENT_TONES[period.status]} />}
          meta={`${num(period.employees)} employees · due ${formatDay(period.due)}`}
          tone={period.status === "Paid" ? "teal" : "warning"}
        />
        <RequestDetailsSection
          title="AMOUNTS"
          fields={[
            { icon: FunctionsOutlined, label: "Payable to the Board", value: money(period.payable), helper: `Employee ${money(period.ee)} + employer ${money(period.er)}`, accent: indigo },
            { icon: CheckRounded, label: "Paid", value: money(period.paid), accent: green },
            { icon: PendingActionsOutlined, label: "Board balance", value: money(period.balance), accent: orange },
            ...(period.outstanding ? [{ icon: ErrorOutlineOutlined, label: "Employee shares not yet recovered", value: money(period.outstanding), helper: "Inside the payable — not added to the balance", accent: pink }] : []),
            ...(period.held ? [{ icon: ErrorOutlineOutlined, label: "Held employees", value: `${period.held} — not in the payable until worked out`, accent: purple }] : []),
            {
              icon: DescriptionOutlined,
              label: "Company LWF statement",
              value: "Not an official return",
              helper: (
                <Mui.Link component="button" underline="hover" onClick={() => downloadStatement(period, companyName)} sx={linkSx}>
                  Download
                </Mui.Link>
              ),
              accent: indigo,
            },
          ]}
        />
        <Mui.Box>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1 }}>Payments</Mui.Typography>
          <SimpleTable
            fit
            heads={[
              { label: "Type", width: 110 },
              { label: "Paid on", width: 100 },
              { label: "Amount (₹)", width: 90 },
              { label: "Reference", width: 150 },
              { label: "Receipt", width: 140 },
              { label: "", width: 80 },
            ]}
          >
            {period.payments.length ? (
              period.payments.map((payment) => (
                <Mui.TableRow key={payment.id} sx={{ opacity: reversed.has(payment.id) ? 0.6 : 1 }}>
                  <Mui.TableCell sx={tableCellSx}>
                    {PAYMENT_TYPES[payment.type]}
                    {reversed.has(payment.id) ? " · reversed" : ""}
                  </Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{formatDay(payment.paid_on)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{num(payment.amount)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{payment.reference}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{payment.receipt || "—"}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    {payment.type !== "REVERSAL" && !reversed.has(payment.id) ? (
                      <Mui.Link component="button" underline="hover" onClick={() => setReverseTarget(payment)} sx={{ ...linkSx, color: "#DC2626" }}>
                        Reverse
                      </Mui.Link>
                    ) : null}
                  </Mui.TableCell>
                </Mui.TableRow>
              ))
            ) : (
              <Mui.TableRow>
                <Mui.TableCell colSpan={6} sx={{ ...tableCellSx, textAlign: "center", color: designColors.slate600 }}>No payment recorded yet.</Mui.TableCell>
              </Mui.TableRow>
            )}
          </SimpleTable>
          <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>
            {period.payments.length ? `Recorded by ${period.payments[period.payments.length - 1].recorded_by} · ${moment(period.payments[period.payments.length - 1].recorded_at).format("DD MMM YYYY, HH:mm")}` : ""}
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
      <GlobalConfirmDialog
        open={Boolean(reverseTarget)}
        title="Reverse payment"
        onCancel={() => setReverseTarget(null)}
        onConfirm={() => {
          onReverse(reverseTarget);
          setReverseTarget(null);
        }}
        confirmLabel="Reverse"
        cancelLabel="Keep"
        loadingLabel="Reversing..."
        maxWidth="sm"
        danger
      >
        Reverse {reverseTarget ? `${money(reverseTarget.amount)} (${reverseTarget.reference})` : ""}? A reversal record is added and the original is kept in the history. Record the correct payment afterwards.
      </GlobalConfirmDialog>
    </GlobalSidePanel>
  );
};

export const LwfPaymentsTab = ({ monthInfo, periods, payments, onRecord, onReverse }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [periodFilter, setPeriodFilter] = useState("24");
  const [panel, setPanel] = useState({ kind: null, id: null });
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "lwf_payments_saved_views",
    fullHeaders: lwfPaymentHeader,
  });

  const rows = periods.filter((period) => inPeriodFilter(period, periodFilter, moment())).sort((a, b) => b.period_key.localeCompare(a.period_key) || a.site.localeCompare(b.site));
  const sum = (list, key) => list.reduce((total, item) => total + item[key], 0);
  const selected = periods.find((period) => period.id === panel.id);

  const renderCell = (row, key) => {
    switch (key) {
      case "site":
        return <Mui.Box component="span" sx={{ fontWeight: designWeights.medium }}>{row.site}</Mui.Box>;
      case "employees":
      case "ee":
      case "er":
      case "payable":
        return num(row[key]);
      case "paid":
        return row.paid ? num(row.paid) : "—";
      case "balance":
        return num(row.balance);
      case "due":
        return formatDay(row.due);
      case "status_label":
        return <ToneChip label={row.status_label} tone={PAYMENT_TONES[row.status]} />;
      case "action":
        return (
          <Mui.Link
            component="button"
            underline="hover"
            onClick={(event) => {
              event.stopPropagation();
              setPanel({ kind: row.balance > 0 ? "record" : "view", id: row.id });
            }}
            sx={linkSx}
          >
            {row.action}
          </Mui.Link>
        );
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid
        cards={[
          { key: "topay", title: "To pay", value: money(sum(rows.filter((row) => row.status === "To pay"), "balance")), icon: PendingActionsOutlined, tone: "amber" },
          { key: "short", title: "Short paid", value: money(sum(rows.filter((row) => row.status === "Partly paid"), "balance")), icon: ErrorOutlineOutlined, tone: "red" },
          { key: "paid", title: `Paid (${monthInfo.label})`, value: money(sum(periods.filter((row) => row.period_key === monthInfo.value), "paid")), icon: CheckRounded, tone: "green" },
          { key: "sites", title: "Sites", value: new Set(rows.map((row) => row.site)).size, icon: PlaceOutlined, tone: "blue" },
        ]}
      />
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <FilterSelect label="Period" value={periodFilter} onChange={setPeriodFilter} options={PERIOD_FILTERS} />
      </Mui.Stack>
      <StatutoryList
        title="LWF Payments"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setPanel({ kind: "view", id: row.id })}
        selectedKey={panel.id}
        itemLabel="payments"
        searchPlaceholder="Search site or reference"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>One row per site per deduction (yearly, half-yearly or monthly).</Mui.Typography>
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Payable = employee share (including any not yet recovered from employees) + employer share. Balance = payable − paid to the Board; employee shares still to recover are inside the payable, not extra. Pay it to the state Labour Welfare Board, then record the payment here. Status is worked out from the amounts: To pay · Partly paid · Paid · Overpaid." />
      </Mui.Box>

      <RecordPanel
        open={panel.kind === "record" && Boolean(selected)}
        period={selected}
        allPayments={payments}
        companyName={topbarCompanyName}
        onClose={() => setPanel({ kind: null, id: null })}
        onSave={(payment) => {
          onRecord(payment);
          setPanel({ kind: "view", id: payment.period });
          showToast("success", `Payment ${money(payment.amount)} recorded for ${selected.site} · ${selected.period_label}.`);
        }}
      />
      <ViewPanel
        open={panel.kind === "view" && Boolean(selected)}
        period={selected}
        companyName={topbarCompanyName}
        onClose={() => setPanel({ kind: null, id: null })}
        onRecord={() => setPanel((current) => ({ ...current, kind: "record" }))}
        onReverse={(payment) => {
          onReverse(payment);
          showToast("success", `${payment.reference} reversed. Record the correct payment next.`);
        }}
      />
    </>
  );
};
